import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Building2, Bot, AlertCircle, CheckCircle, Clock, Users } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

interface DashboardStats {
  totalTenants: number;
  activeBots: number;
  pendingApprovals: number;
  openEscalations: number;
  tenantsInIntake: number;
  tenantsLive: number;
}

const AdminDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [
          tenantsResult,
          botsResult,
          approvalsResult,
          escalationsResult,
        ] = await Promise.all([
          supabase.from('advisory_tenants').select('id, status'),
          supabase.from('bots').select('id, status').eq('status', 'live'),
          supabase.from('approvals').select('id, status').eq('status', 'pending'),
          supabase.from('escalations').select('id').is('campaign_id', null),
        ]);

        const tenants = tenantsResult.data || [];
        
        setStats({
          totalTenants: tenants.length,
          activeBots: botsResult.data?.length || 0,
          pendingApprovals: approvalsResult.data?.length || 0,
          openEscalations: escalationsResult.data?.length || 0,
          tenantsInIntake: tenants.filter(t => t.status === 'intake').length,
          tenantsLive: tenants.filter(t => t.status === 'live').length,
        });
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    { 
      title: 'Total Tenants', 
      value: stats?.totalTenants || 0, 
      icon: Building2,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    { 
      title: 'Live Bots', 
      value: stats?.activeBots || 0, 
      icon: Bot,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    { 
      title: 'Pending Approvals', 
      value: stats?.pendingApprovals || 0, 
      icon: Clock,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
    },
    { 
      title: 'Open Escalations', 
      value: stats?.openEscalations || 0, 
      icon: AlertCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-500/10',
    },
    { 
      title: 'In Intake', 
      value: stats?.tenantsInIntake || 0, 
      icon: Users,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    { 
      title: 'Live Tenants', 
      value: stats?.tenantsLive || 0, 
      icon: CheckCircle,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Overview of advisory assistants platform
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <Card key={card.title}>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    {card.title}
                  </CardTitle>
                  <div className={`p-2 rounded-lg ${card.bgColor}`}>
                    <Icon className={`w-4 h-4 ${card.color}`} />
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <Skeleton className="h-8 w-16" />
                  ) : (
                    <p className="text-2xl font-bold">{card.value}</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Recent Activity Placeholder */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activity</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground text-sm">
              Activity feed will be displayed here showing recent tenant signups, 
              bot deployments, and escalations.
            </p>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
